import React, { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getCookie } from 'helper/cookie';
import { Spin, Row, Col, Menu, Card, Typography, Modal } from 'antd'; // Added Modal for popup
import { get } from 'lodash';
import { getVideoTutorialAll } from 'api/videoTutorial'; // API for fetching video tutorials
import { getCategoryTutorialAll } from 'api/categoryTutorial'; // API for fetching categories
import { FolderOutlined } from '@ant-design/icons'; // Ant Design icon
import styled from 'styled-components';
import { motion } from 'framer-motion';

const { Title } = Typography;

const StyledSidebar = styled.div`
  background: linear-gradient(135deg, #e6f7ff, #ffffff);
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(0, 115, 230, 0.2);
  padding: 16px;
`;

const StyledCard = styled(Card)`
  border-radius: 16px !important;
  background: #f9f9f9 !important;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 115, 230, 0.2);
  }
`;

const StyledMenu = styled(Menu)`
  background: transparent !important;
  border: none !important;

  .ant-menu-item-selected {
    background-color: #e6f7ff !important;
    border-radius: 8px;
    color: #1890ff !important;
  }

  .ant-menu-item {
    border-radius: 8px;
    transition: background 0.2s ease;
    &:hover {
      background-color: #f0f5ff;
    }
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 16px;
    padding: 0;
    overflow: hidden;
  }

  .ant-modal-body {
    padding: 24px;
  }

  .ant-modal-header {
    border-bottom: none;
    padding: 24px;
    background: linear-gradient(90deg, #0073e6, #00b894);
    color: white;

    .ant-modal-title {
      color: white;
      font-weight: 600;
    }
  }
`;

const TutorialComponents = () => {
  const isMobileView = useSelector(state => get(state, 'device.isMobileView', false));
  const navigate = useNavigate();
  const token = getCookie('access_tokenltl');
  const [isLoading, setIsLoading] = useState(false);
  const [menuData, setMenuData] = useState([]); // Category Menu data on the left
  const [selectedContent, setSelectedContent] = useState([]); // Content for the selected video tutorial
  const [data, setData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false); // Modal visibility state
  const [currentContent, setCurrentContent] = useState<any>(null); // Content to display in the modal
  const [defaultSelectedKey, setDefaultSelectedKey] = useState<string>('1'); // For default menu selection
  const dataUser: any = getCookie('dataUser');
  const parseDataUser = dataUser ? JSON.parse(dataUser) : null;

  // Fetch the category list for the menu (left side)
  const fetchCategoryTutorialList = useCallback(async () => {
    setIsLoading(true);
    const params = {
      limit: 999,
      offset: 0,
      order_by: 'id',
      order_type: 'ASC',
      category_tutorial_status: 'Y',
      category_tutorial_view_type: parseDataUser?.type,
    };
    try {
      const responseData = await getCategoryTutorialAll({ token, params });
      if (responseData?.status === 200) {
        const { data } = responseData;
        setData(data?.data?.category_tutorial); // Set category tutorial data
        setMenuData(data?.data?.category_tutorial); // Set the menu data
        if (data?.data?.category_tutorial?.length > 0) {
          const firstCategoryId = data?.data?.category_tutorial[0]?.id; // Get the first category id
          setDefaultSelectedKey(String(firstCategoryId)); // Set the first category as default selected key
          fetchVideoTutorialList(firstCategoryId); // Fetch content for the first category by default
        }
        setIsLoading(false);
      } else {
        setData([]);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchCategoryTutorialList();
    }
  }, [token, fetchCategoryTutorialList]);

  // Fetch video tutorials by category_tutorial_id (right side content)
  const fetchVideoTutorialList = useCallback(
    async (categoryId: any) => {
      setIsLoading(true);
      try {
        const responseData = await getVideoTutorialAll({
          token,
          params: {
            category_tutorial_id: categoryId,
            limit: 999,
            offset: 0,
            video_tutorial_status: 'Y',
          },
        });
        if (responseData?.status === 200) {
          const { video_tutorial } = responseData.data.data;
          setSelectedContent(video_tutorial); // Set video tutorial content
          setIsLoading(false);
        } else {
          setSelectedContent([]);
          setIsLoading(false);
        }
      } catch (error) {
        setIsLoading(false);
      }
    },
    [token],
  );

  // Handle click on the menu item
  const handleMenuClick = ({ key }: any) => {
    fetchVideoTutorialList(key); // Fetch video tutorial content based on category_tutorial_id
  };

  // Handle clicking on a content card to show modal
  const handleContentClick = (content: any) => {
    setCurrentContent(content); // Set the content for the modal
    setIsModalVisible(true); // Show the modal
  };

  // Handle closing the modal
  const handleCloseModal = () => {
    setIsModalVisible(false); // Close the modal
    setCurrentContent(null); // Reset current content
  };

  return (
    <div style={{ padding: '20px' }} className="table table-responsive">
      <Row gutter={16}>
        {/* Left column for the menu */}
        <Col xs={24} sm={10} md={8} lg={7} xl={6}>
          <div style={{ border: '1px solid #d9d9d9', borderRadius: '8px', padding: '10px', backgroundColor: '#fafafa' }}>
            <StyledSidebar>
              <StyledMenu
                mode="vertical"
                onClick={handleMenuClick}
                defaultSelectedKeys={[defaultSelectedKey]}
                style={{ height: '100%', borderRight: 0 }}
              >
                {data?.map((item: any) => (
                  <Menu.Item key={item.id} icon={<FolderOutlined />}>
                    {item.category_tutorial_name}
                  </Menu.Item>
                ))}
              </StyledMenu>
            </StyledSidebar>
          </div>
        </Col>

        {/* Right column for the content */}
        <Col xs={24} sm={14} md={16} lg={17} xl={18}>
          <div
            style={{
              border: '1px solid #d9d9d9',
              borderRadius: '12px',
              padding: '30px',
              background: '#fff',
              minHeight: '300px',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            }}
            className="table table-responsive"
          >
            {isLoading ? (
              <Spin size="large" />
            ) : (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                {selectedContent && selectedContent.length > 0 ? (
                  <Row gutter={[16, 16]}>
                    {selectedContent.map((tutorial: any, index: number) => {
                      const match = tutorial.video_tutorial_detail?.match(/<iframe[^>]+src="([^"]+)"[^>]*>/);
                      const iframeSrc = match?.[1] ?? null;

                      return (
                        <Col key={index} xs={24} sm={12} md={8}>
                          <StyledCard
                            title={
                              <Title level={5} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {tutorial.video_tutorial_name}
                              </Title>
                            }
                            bordered={false}
                            hoverable
                            onClick={() => handleContentClick(tutorial)}
                          >
                            {iframeSrc ? (
                              <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                                <iframe
                                  src={iframeSrc}
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '8px',
                                  }}
                                ></iframe>
                              </div>
                            ) : (
                              <div style={{ padding: '8px 0', color: '#888' }}>คลิกเพื่อดูข้อมูล</div>
                            )}
                          </StyledCard>
                        </Col>
                      );
                    })}
                  </Row>
                ) : (
                  <div>กรุณาเลือกหมวดหมู่เพื่อดูวิดีโอ</div>
                )}
              </motion.div>
            )}
          </div>
        </Col>
      </Row>

      {/* Modal for content details */}
      <StyledModal
        title={currentContent?.video_tutorial_name}
        visible={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={1000}
        bodyStyle={{
          maxHeight: '80vh',
          overflowY: 'auto',
        }}
      >
        {currentContent && (
          <div style={{ width: '100%' }}>
            <div
              style={{ maxWidth: '100%', overflowX: 'auto', wordBreak: 'break-word' }}
              dangerouslySetInnerHTML={{ __html: currentContent.video_tutorial_detail }}
            />
          </div>
        )}
      </StyledModal>
    </div>
  );
};

export default TutorialComponents;
